import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Space, Typography } from "antd";
import { Button, PhoneInput } from "@libertify/ui";
import { isValidPhoneNumber } from "libphonenumber-js";
import type { UseMutationResult } from "@tanstack/react-query";
import type { MfaFactor, MfaFactorStatus } from "../types";
import { getMfaErrorDetail } from "../utils";
import { DisableFactorButton } from "./DisableFactorButton";
import { MfaOtpInput } from "./MfaOtpInput";

export interface SmsFactorCardProps {
  factor: MfaFactorStatus;
  startSms: UseMutationResult<unknown, unknown, string>;
  confirmSms: UseMutationResult<unknown, unknown, string>;
  disableFactor: UseMutationResult<unknown, unknown, MfaFactor>;
  // Called after a successful verify/disable so the parent collapse panel closes.
  onCollapse: () => void;
}

// "idle" (Set up button) -> "phone" (PhoneInput) -> "code" (Input.OTP)
type Step = "idle" | "phone" | "code";

export const SmsFactorCard = ({
  factor,
  startSms,
  confirmSms,
  disableFactor,
  onCollapse,
}: SmsFactorCardProps) => {
  const t = useTranslate();
  const [step, setStep] = useState<Step>("idle");
  const [phone, setPhone] = useState("");
  // Server-side error for whichever step is currently shown (phone or code)
  // — the two steps never render at once, so one field covers both.
  const [error, setError] = useState<string | null>(null);

  const isPhoneValid = phone !== "" && isValidPhoneNumber(phone);
  const phoneErrorMessage =
    error ||
    (phone !== "" && !isPhoneValid
      ? t("pages.settings.security.factors.Sms.invalidPhone")
      : undefined);

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setError(null);
  };

  const handleSendCode = () => {
    startSms.mutate(phone, {
      onSuccess: () => setStep("code"),
      onError: (mutationError) => {
        // 422 is pre-empted client-side by isValidPhoneNumber above — this
        // is only a fallback in case the backend still rejects the number,
        // shown inline under the field rather than as a toast.
        setError(
          getMfaErrorDetail(mutationError)?.message ||
            t("pages.settings.security.factors.Sms.invalidPhone")
        );
      },
    });
  };

  const handleVerify = (code: string) => {
    setError(null);
    confirmSms.mutate(code, {
      onSuccess: () => {
        setStep("idle");
        setPhone("");
        onCollapse();
      },
      onError: (mutationError) => {
        setError(
          getMfaErrorDetail(mutationError)?.message ||
            t("pages.settings.security.invalidCode")
        );
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        {t("pages.settings.security.factors.Sms.description")}
      </Typography.Text>

      {factor.enabled && (
        <DisableFactorButton
          factor="Sms"
          disableFactor={disableFactor}
          onCollapse={onCollapse}
        />
      )}

      {!factor.enabled && step === "idle" && (
        <Button variant="primary" onClick={() => setStep("phone")}>
          {t("pages.settings.security.setUp")}
        </Button>
      )}

      {!factor.enabled && step === "phone" && (
        <>
          <PhoneInput
            label={t("pages.settings.security.factors.Sms.phoneLabel")}
            value={phone}
            onChange={handlePhoneChange}
            status={phoneErrorMessage ? "error" : "default"}
            helpText={phoneErrorMessage}
          />
          <Button
            variant="primary"
            disabled={!isPhoneValid}
            loading={startSms.isPending}
            onClick={handleSendCode}
          >
            {t("pages.settings.security.factors.Sms.sendCode")}
          </Button>
        </>
      )}

      {!factor.enabled && step === "code" && (
        <Flex vertical gap={10} align="center">
          <MfaOtpInput
            promptText={t("pages.settings.security.factors.Sms.enterCode")}
            error={error}
            isPending={confirmSms.isPending}
            onComplete={handleVerify}
          />
        </Flex>
      )}
    </Space>
  );
};
